import React, { useCallback, useMemo, useState } from 'react';
import {
  FlatList,
  Keyboard,
  Modal,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { TextInput } from 'react-native-paper';
import { AppAlert } from '../../../../services/AppAlert';
import KeyboardAwareFormScroll from '../../../../components/Reusables/KeyboardAwareFormScroll';
import { useTheme, type Theme } from '../../../../theme';
import { WEB_MODAL_MAX_WIDTH } from '../../../../theme/webLayoutWidths';
import { borderWidth, fs, s, v } from '../../../../utils/libs';

/**
 * Select / search / add category modal — shared by NewProduct and the
 * wizard first-product step (same UX as NewProduct's inline modal).
 */

export interface CategorySelectModalProps {
  visible: boolean;
  categories: string[];
  onRequestClose: () => void;
  onSelectCategory: (name: string) => void;
  /** Called when the user adds a category that was not already in the list. */
  onAddCategory: (name: string) => void;
}

const CategorySelectModal: React.FC<CategorySelectModalProps> = ({
  visible,
  categories,
  onRequestClose,
  onSelectCategory,
  onAddCategory,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [query, setQuery] = useState('');
  const [showList, setShowList] = useState(true);

  const webPlaceholderTheme = useMemo(() => {
    if (Platform.OS !== 'web') return {};
    return {
      theme: {
        colors: {
          onSurfaceVariant: 'rgba(107, 114, 128, 0.55)',
          placeholder: 'rgba(107, 114, 128, 0.55)',
        },
      },
    };
  }, []);

  const filteredCategories = useMemo(() => {
    if (!query || query.trim().length === 0) {
      return categories;
    }
    const searchTerm = query.trim().toLowerCase();
    const matches = categories.filter((category: string) =>
      category.toLowerCase().includes(searchTerm),
    );
    return matches.sort((a: string, b: string) => {
      const aName = a.toLowerCase();
      const bName = b.toLowerCase();
      if (aName === searchTerm && bName !== searchTerm) return -1;
      if (bName === searchTerm && aName !== searchTerm) return 1;
      const aStartsWith = aName.startsWith(searchTerm);
      const bStartsWith = bName.startsWith(searchTerm);
      if (aStartsWith && !bStartsWith) return -1;
      if (bStartsWith && !aStartsWith) return 1;
      return aName.localeCompare(bName);
    });
  }, [categories, query]);

  const resetAndClose = useCallback(() => {
    setQuery('');
    setShowList(true);
    onRequestClose();
  }, [onRequestClose]);

  const commitCategory = useCallback(
    (rawName: string) => {
      const name = (rawName || '').trim();
      if (name.length < 2) {
        AppAlert.alert('Error', 'Enter a valid category name');
        return;
      }
      const exists = (categories || []).some(
        (c: string) => (c || '').toLowerCase() === name.toLowerCase(),
      );
      if (!exists) {
        onAddCategory(name);
      }
      onSelectCategory(name);
      setQuery('');
      setShowList(true);
    },
    [categories, onAddCategory, onSelectCategory],
  );

  const renderCategoryItem = useCallback(
    ({ item }: { item: string }) => (
      <TouchableOpacity
        onPress={() => {
          Keyboard.dismiss();
          onSelectCategory(item);
          setQuery('');
          setShowList(true);
        }}
        style={styles.subcategoryItem}>
        <Text style={styles.subcategoryItemText}>{item}</Text>
      </TouchableOpacity>
    ),
    [onSelectCategory, styles.subcategoryItem, styles.subcategoryItemText],
  );

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      statusBarTranslucent={true}
      presentationStyle="overFullScreen"
      onRequestClose={resetAndClose}
      onShow={() => {
        setQuery('');
        setShowList(true);
      }}>
      <KeyboardAwareFormScroll
        contentContainerStyle={styles.popupOverlay}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        baseBottomPadding={v(20)}
        bottomOffset={v(20)}>
        <View style={[styles.popupContainer, { width: '90%' }]}>
          <Text style={styles.popupTitle}>Select Category</Text>
          {showList && filteredCategories?.length ? (
            <View style={{ maxHeight: v(220) }}>
              <FlatList
                data={filteredCategories}
                keyExtractor={(item, idx) => `${item}-${idx}`}
                renderItem={renderCategoryItem}
                keyboardShouldPersistTaps="handled"
                initialNumToRender={12}
                windowSize={5}
                showsHorizontalScrollIndicator
                maxToRenderPerBatch={12}
                removeClippedSubviews={false}
              />
            </View>
          ) : null}

          <View style={{ marginTop: v(12) }}>
            <Text style={styles.addNewLabel}>Or add new</Text>
            <TextInput
              mode="outlined"
              value={query}
              onChangeText={text => {
                setQuery(text);
                setShowList(true);
              }}
              placeholder="Search or add new category"
              placeholderTextColor={theme.colors.textGray500}
              {...webPlaceholderTheme}
              style={styles.input}
              returnKeyType="done"
              onSubmitEditing={() => commitCategory(query)}
            />

            <View style={styles.modalBtnRow}>
              <TouchableOpacity
                style={[styles.cancelButton, { flex: 1 }]}
                onPress={resetAndClose}>
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => commitCategory(query)}
                style={[styles.addButton, { flex: 1 }]}>
                <Text style={styles.addButtonText}>+ Add</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </KeyboardAwareFormScroll>
    </Modal>
  );
};

const createStyles = (theme: Theme) => {
  const isWeb = Platform.OS === 'web';
  return StyleSheet.create({
    popupOverlay: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0,0,0,0.5)',
    },
    popupContainer: {
      width: '80%',
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      padding: s(15),
      ...(isWeb && {
        maxWidth: WEB_MODAL_MAX_WIDTH,
        width: '90%',
      }),
    },
    popupTitle: {
      fontSize: fs(18),
      lineHeight: fs(20),
      fontWeight: 'bold',
      marginBottom: v(15),
      textAlign: 'center',
      color: theme.colors.text,
    },
    addNewLabel: {
      fontWeight: '700',
      marginBottom: v(10),
      fontSize: fs(14),
      color: theme.colors.text,
    },
    input: {
      backgroundColor: theme.colors.surface,
      marginBottom: v(8),
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderSecondary,
    },
    modalBtnRow: {
      flexDirection: 'row',
      gap: s(8),
      marginTop: v(10),
    },
    subcategoryItem: {
      paddingVertical: s(10),
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.borderGray200,
    },
    subcategoryItemText: {
      fontSize: fs(16),
      color: theme.colors.text,
    },
    cancelButton: {
      marginTop: v(15),
      padding: s(8),
      backgroundColor: theme.colors.errorDark,
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
    },
    cancelButtonText: {
      color: theme.colors.textInverse,
    },
    addButton: {
      marginTop: v(15),
      padding: s(8),
      backgroundColor: theme.colors.primaryDark,
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
    },
    addButtonText: {
      color: theme.colors.textInverse,
      fontWeight: 'bold',
    },
  });
};

export default CategorySelectModal;
